import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "",
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "",
  appId: import.meta.env.VITE_FIREBASE_APP_ID || ""
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Get VAPID key from environment
const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY || "";

// Initialize messaging (only in browser)
let messaging: ReturnType<typeof getMessaging> | null = null;

export const initializeMessaging = async () => {
  if (typeof window === 'undefined') return null;
  
  const supported = await isSupported();
  if (!supported) {
    console.log('Firebase Messaging is not supported in this browser');
    return null;
  }
  
  if (!messaging) {
    messaging = getMessaging(app);
  }
  
  return messaging;
};

export const requestNotificationPermission = async (): Promise<string | null> => {
  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.log('Notification permission denied');
      return null;
    }

    const messagingInstance = await initializeMessaging();
    if (!messagingInstance) {
      console.error('Messaging not initialized');
      return null;
    }

    // Register service worker
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    console.log('Service Worker registered:', registration);

    // Get FCM token
    const token = await getToken(messagingInstance, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration
    });

    console.log('FCM Token obtained:', token);
    return token;
  } catch (error) {
    console.error('Error getting notification permission:', error);
    return null;
  }
};

export const onForegroundMessage = async (callback: (payload: any) => void) => {
  console.log('[Firebase] Setting up foreground message handler...');
  
  // Ensure messaging is initialized
  const messagingInstance = await initializeMessaging();
  
  if (!messagingInstance) {
    console.warn('[Firebase] Messaging not initialized, cannot setup foreground handler');
    return () => {};
  }
  
  console.log('[Firebase] Foreground handler configured successfully');
  
  return onMessage(messagingInstance, (payload) => {
    console.log('[Firebase] Foreground message received:', payload);
    callback(payload);
  });
};

export { app, messaging };
